import { authFetch } from "@/utils/authFetch";
import { toast } from "sonner";

const DB_NAME = "selfcertify_sync";
const DB_VERSION = 1;
const STORE_NAME = "queue";
const API = process.env.REACT_APP_BACKEND_URL;

function openSyncDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "id", autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Add a failed certificate submission to the sync queue.
 */
export async function queueForSync({ endpoint, method, payload, formTitle }) {
  const db = await openSyncDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).add({
      endpoint,
      method: method || "POST",
      payload,
      formTitle: formTitle || "Certificate",
      queuedAt: new Date().toISOString(),
      retries: 0,
    });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Get all pending items in the sync queue.
 */
export async function getPendingSync() {
  const db = await openSyncDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Remove a synced item from the queue.
 */
async function removeSyncItem(id) {
  const db = await openSyncDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Increment retry count for a failed item.
 */
async function incrementRetry(item) {
  const db = await openSyncDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put({ ...item, retries: (item.retries || 0) + 1 });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Process all pending sync items. Returns { synced, failed }.
 */
export async function processSyncQueue() {
  if (!navigator.onLine) return { synced: 0, failed: 0 };

  const pending = await getPendingSync();
  if (pending.length === 0) return { synced: 0, failed: 0 };

  let synced = 0;
  let failed = 0;

  for (const item of pending) {
    // Skip items with too many retries
    if (item.retries >= 5) {
      failed++;
      continue;
    }

    try {
      const res = await authFetch(`${API}${item.endpoint}`, {
        method: item.method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(item.payload),
      });

      if (res.ok) {
        await removeSyncItem(item.id);
        synced++;
      } else if (res.status === 402) {
        // Insufficient credits — keep in queue but don't retry endlessly
        await incrementRetry(item);
        failed++;
      } else {
        await incrementRetry(item);
        failed++;
      }
    } catch {
      // Still offline or network error
      failed++;
    }
  }

  return { synced, failed };
}

/**
 * Clear a specific item from the queue (user manually dismisses).
 */
export async function clearSyncItem(id) {
  await removeSyncItem(id);
}

/**
 * Clear all items from the queue.
 */
export async function clearSyncQueue() {
  const db = await openSyncDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Start listening for online events and auto-sync.
 */
let listenerAttached = false;
export function startSyncListener() {
  if (listenerAttached) return;
  listenerAttached = true;

  const handleOnline = async () => {
    const pending = await getPendingSync();
    if (pending.length === 0) return;

    toast.info(`Back online — syncing ${pending.length} pending certificate${pending.length > 1 ? "s" : ""}...`);
    const { synced, failed } = await processSyncQueue();
    if (synced > 0) {
      toast.success(`${synced} certificate${synced > 1 ? "s" : ""} synced successfully!`);
    }
    if (failed > 0) {
      toast.error(`${failed} certificate${failed > 1 ? "s" : ""} failed to sync. Will retry later.`);
    }
  };

  window.addEventListener("online", handleOnline);
}
